import { create } from "zustand";
import { LOG } from "../utils/logUtil";
import axios from "axios";
import { useAuth } from "./useAuth";
import { useAppLoading } from "./useAppLoading";
import { IUser } from "./useUsers";

interface IAppInfo {
  _id?: string;
  title?: string;
  en_title?: string;
  nl_title?: string;
  image?: string;
  color?: string;
  creator?: IUser;
  richText?: string;
  en_richText?: string;
  nl_richText?: string;
  /** Optional YouTube URL shown in the mobile detail sheet's iframe player. */
  videoUrl?: string | null;
  order?: number;
  createdAt?: Date;
  updatedAt?: Date;
}

interface IStore {
  /** State */
  appInfo: IAppInfo | null;
  appInfos: IAppInfo[];
  addedAppInfo?: string | null;
  updatedAppInfo?: string | null;
  /** Callbacks */
  updateAppInfosOrder: (data: { _id: string, order: number }[]) => void;
  getAppInfos: () => void;
  getAppInfo: (id: string) => void;
  addAppInfo: (body: IAppInfo) => void;
  deleteAppInfo: (id: string) => void;
  updateAppInfo: (id: string, body: IAppInfo) => void;
  clearAddedAppInfo: () => void;
  clearUpdatedAppInfo: () => void;
}

export const useAppInfo = create<IStore>()((set) => ({
  /** State */
  appInfos: [],
  appInfo: null,
  addedAppInfo: null,
  updatedAppInfo: null,
  /** Callbacks */
  updateAppInfosOrder: async (body) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.post("appinfo/update-order", body);
      if (response?.data) {
        set({ appInfos: response.data });
      }
    } catch (error) {
      LOG.error("Error on updating App Infos Order", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getAppInfos: async () => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get("appinfo");
      if (response?.data) {
        set({ appInfos: response.data });
      }
    } catch (error) {
      LOG.error("Error on fetching App Infos", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getAppInfo: async (id) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(`appinfo/${id}`);
      set({ appInfo: response.data });
    } catch (error) {
      LOG.error("Error on fetching App Info", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  addAppInfo: async (body) => {
    useAppLoading.getState().setIsLoading(true);
    const creator = useAuth.getState().user?._id;
    try {
      const response = await axios.post("appinfo", {
        ...body,
        creator,
      });
      if (response?.data) {
        const newAppInfo = response.data;
        set((state) => ({
          appInfos: [newAppInfo, ...state.appInfos],
          addedAppInfo: newAppInfo._id,
        }));
      }
    } catch (error) {
      LOG.error("Error on adding new App Info", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  deleteAppInfo: async (id) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      await axios.delete(`appinfo/${id}`);
      set((state) => ({
        appInfos: state.appInfos.filter((appInfo) => appInfo._id !== id),
      }));
    } catch (error) {
      LOG.error("Error on deleting an App Info", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  updateAppInfo: async (id, body) => {
    useAppLoading.getState().setIsLoading(true);
    const creator = useAuth.getState().user?._id;
    try {
      const response = await axios.put(`appinfo/${id}`, {
        ...body,
        creator,
      });
      if (response?.data) {
        const updatedAppInfo = response.data;
        set((state) => ({
          appInfos: state.appInfos.map((appInfo) => {
            if (appInfo._id === updatedAppInfo._id) {
              return updatedAppInfo;
            }
            return appInfo;
          }),
          updatedAppInfo: updatedAppInfo._id,
        }));
      }
    } catch (error) {
      LOG.error("Error on updating an Article", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  clearAddedAppInfo: () => {
    set({ addedAppInfo: null });
  },
  clearUpdatedAppInfo: () => {
    set({ updatedAppInfo: null });
  },
}));
